import React, {
	useRef,
	useState,
	useEffect,
	useMemo,
	useCallback,
	type SyntheticEvent,
} from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	AiDraftPreviewState,
	ConfluenceSpace,
	aiDraftArticleGenerationState,
} from '@atlassian/jira-servicedesk-knowledge-common/src/common/types.tsx';
import { useNavigateToEditor } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/hooks/use-navigate-to-editor/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import RenderNextAnimationFrame from '@atlassian/jira-servicedesk-knowledge-common/src/common/ui/render-animation-frame.tsx';
import { TEAM_NAME, PACKAGE_NAME } from '../../../common/constants.tsx';
import { CreateArticleDrawerEntryPoint } from './create-article-drawer/entrypoint.tsx';
import messages from './messages.tsx';
import { AiDraftGenChangeBoarding } from './ai-draft-gen-change-boarding/index.tsx';

export type CreateArticleProps = {
	topicId: string;
	topicName: string;
	numberOfRelatedTickets: number;
	hasConfluenceLicense: boolean;
	spaces: ConfluenceSpace[];
	aiDraftArticleGenerationState: aiDraftArticleGenerationState;
	onToggleRightDrawer: (isOpen: boolean) => void;
	isRightDrawerOpen: boolean;
	getAIDraftContent: (topicId: string) => void;
	aiDraftContent: AiDraftPreviewState | undefined;
	rowIndex: number;
};

export const CreateArticleAction = ({
	topicId,
	hasConfluenceLicense,
	spaces,
	topicName,
	numberOfRelatedTickets,
	aiDraftArticleGenerationState,
	onToggleRightDrawer,
	isRightDrawerOpen,
	getAIDraftContent,
	aiDraftContent,
	rowIndex,
}: CreateArticleProps) => {
	const { formatMessage } = useIntl();
	const [isDrawerOpen, toggleDrawer] = useState(false);
	const createArticleButtonRef = useRef<HTMLElement>(null);
	const initialRender = useRef(true);
	const { navigateToEditor } = useNavigateToEditor({ spaces, topicId, topicName });
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!initialRender.current && !isDrawerOpen && createArticleButtonRef.current) {
			createArticleButtonRef.current.focus();
		}
		initialRender.current = false;
	}, [isDrawerOpen]);

	const onClose = useCallback(
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			toggleDrawer(false);
			onToggleRightDrawer(false);
			fireUIAnalytics(analyticsEvent, 'createArticleDrawer closed');
		},
		[toggleDrawer, onToggleRightDrawer],
	);

	const showDrawer = () => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'createArticleBtnForTopic clicked', {
			triggeredBy: 'actions-create-article-button',
			hasConfluenceLicense,
			topicId,
			aiDraftArticleGenerationState,
		});

		if (spaces.length === 1 && aiDraftArticleGenerationState !== 'COMPLETED') {
			navigateToEditor();
		} else {
			toggleDrawer(true);
			onToggleRightDrawer(true);
		}
	};

	const entryPointProps = useMemo(
		() => ({
			topicId,
			topicName,
			numberOfRelatedTickets,
			spaces,
			aiDraftArticleGenerationState,
			onClose,
			getAIDraftContent,
			aiDraftContent,
		}),
		[
			topicId,
			topicName,
			numberOfRelatedTickets,
			spaces,
			aiDraftArticleGenerationState,
			onClose,
			getAIDraftContent,
			aiDraftContent,
		],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		CreateArticleDrawerEntryPoint,
		entryPointProps,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);

	return (
		<>
			{rowIndex === 0 ? (
				<SpotlightManager blanketIsTinted={false}>
					<SpotlightTarget name="aiDraftGenChangeBoarding">
						<Button
							appearance="link"
							onClick={showDrawer}
							ref={mergeRefs(createArticleButtonRef, triggerRef)}
							isDisabled={isRightDrawerOpen}
							interactionName="jsm-kb-view-ai-draft-drawer"
						>
							{formatMessage(messages.viewDraft)}
						</Button>
					</SpotlightTarget>
					<RenderNextAnimationFrame delay={2000}>
						{() => <AiDraftGenChangeBoarding />}
					</RenderNextAnimationFrame>
				</SpotlightManager>
			) : (
				<Button
					appearance="link"
					onClick={showDrawer}
					ref={mergeRefs(createArticleButtonRef, triggerRef)}
					isDisabled={isRightDrawerOpen}
					interactionName="jsm-kb-view-ai-draft-drawer"
				>
					{formatMessage(messages.viewDraft)}
				</Button>
			)}
			{isDrawerOpen && (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="createArticleDrawerEntryPoint"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					runtimeProps={{
						onClose,
						topicId,
						topicName,
						numberOfRelatedTickets,
						spaces,
						aiDraftArticleGenerationState,
						getAIDraftContent,
						aiDraftContent,
					}}
				/>
			)}
		</>
	);
};
